import { Checkbox, DatePicker, Modal, Switch } from "antd";
import { useEffect, useState, useMemo, useRef } from "react";
import toast from "../../utils/swalToast";
import API_LINK from "../links/Link";
import Resizer from 'react-image-file-resizer';
import moment from "moment";

export default function AppointmentModal({ isVisible, setIsVisible, doctor }) {
    const [step, setStep] = useState('1');
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [selectedTimeHours, setSelectedTimeHours] = useState([]);
    const [selectedHour, setSelectedHour] = useState('');
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [isInsurance, setIsInsurance] = useState(true);
    const [insuranceProvider, setInsuranceProvider] = useState('');
    const [base64Image, setBase64Image] = useState('');
    const [consent1, setConsent1] = useState(true);
    const [consent2, setConsent2] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);

    // Refs for auto-scrolling
    const timeSlotsRef = useRef(null);
    const hoursRef = useRef(null);
    const insuranceRef = useRef(null);
    const continueRef = useRef(null);

    useEffect(() => {
        if (isVisible && doctor) {
            setSelectedTime(null);
            setSelectedDate(null);
            setSelectedHour('');
            setSelectedTimeHours([]);
            setStep('1');
            setInsuranceProvider('');
            setIsInsurance(true);
            setConsent1(true);
            setConsent2(true);
        }
    }, [doctor, isVisible]);

    // Auto-scroll when date is selected
    useEffect(() => {
        if (selectedDate && timeSlotsRef.current) {
            setTimeout(() => {
                timeSlotsRef.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'center' 
                });
            }, 300);
        }
    }, [selectedDate]);

    // Auto-scroll when time slot is selected
    useEffect(() => {
        if (selectedTime && hoursRef.current) {
            setTimeout(() => {
                hoursRef.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'center' 
                });
            }, 300);
        }
    }, [selectedTime]);

    // Auto-scroll when hour is selected
    useEffect(() => {
        if (selectedHour && insuranceRef.current) {
            setTimeout(() => {
                insuranceRef.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'center' 
                });
            }, 300);
        }
    }, [selectedHour]);

    // Auto-scroll to continue button when form is ready
    useEffect(() => {
        if (selectedHour && consent1 && consent2 && continueRef.current) {
            setTimeout(() => {
                continueRef.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'center' 
                });
            }, 500);
        }
    }, [selectedHour, consent1, consent2]);

    // Real-time 48-hour check when date is selected
    useEffect(() => {
        if (selectedDate) {
            const selected = selectedDate.clone().endOf('day'); // Check end of selected day
            const now = moment();
            const diffHours = selected.diff(now, 'hours', true);
            
            console.log('=== Real-time 48 Hour Check (Date Selected) ===');
            console.log('Selected Date (end of day):', selected.format('YYYY-MM-DD HH:mm'));
            console.log('Current:', now.format('YYYY-MM-DD HH:mm'));
            console.log('Diff hours:', diffHours);
            
            if (diffHours < 48) {
                console.log('❌ Date is within 48 hours - showing warning');
                setTimeout(() => {
                    toast.warning(`Because the selected appointment is very soon, please call the office at 404-795-6474 to see if it is available.`);
                    // Reset all selections
                    setSelectedDate(null);
                    setSelectedTime(null);
                    setSelectedHour('');
                    setSelectedTimeHours([]);
                }, 100);
            }
        }
    }, [selectedDate]);

    const submitNext = async () => {
        try {
            if (!selectedDate) {
                toast.warning('Please select a date');
                return;
            } else if (!selectedTime) {
                toast.warning('Please select a time slot');
                return;
            } else if (!selectedHour) {
                toast.warning('Please select a specific hour');
                return;
            } else if (isInsurance && !insuranceProvider) {
                toast.warning('Please enter your insurance provider');
                return;
            } else if (!consent1 || !consent2) {
                toast.warning('Please accept the consent terms');
                return;
            } else {
                // Check 48 hour rule before proceeding to step 2
                const selected = selectedDate.clone();
                const now = moment();
                
                console.log('=== 48 Hour Check ===');
                console.log('Selected Date:', selectedDate.format('YYYY-MM-DD'));
                console.log('Selected Hour:', selectedHour);
                
                if (selectedHour) {
                    const parsedHour = moment(selectedHour, ["h:mm A", "HH:mm"], true);
                    console.log('Parsed Hour valid?', parsedHour.isValid());
                    if (parsedHour.isValid()) {
                        selected.set({ 
                            hour: parsedHour.hour(), 
                            minute: parsedHour.minute(), 
                            second: 0, 
                            millisecond: 0 
                        });
                    }
                }
                
                console.log('Current Time:', now.format('YYYY-MM-DD HH:mm'));
                console.log('Selected Time:', selected.format('YYYY-MM-DD HH:mm'));
                
                const diffHours = selected.diff(now, 'hours', true);
                console.log('Difference in hours:', diffHours);
                
                if (diffHours < 48) {
                    console.log('❌ Appointment is within 48 hours - BLOCKED');
                    toast.warning(`Because the selected appointment is very soon, please call the office at 404-795-6474 to see if it is available.`);
                    return;
                }
                
                console.log('✅ Appointment is more than 48 hours away - ALLOWED');
                setStep('2');
            }
        } catch (error) {
            console.log(error);
        }
    };

    const submitAppointment = async () => {
        if (!name) {
            toast.warning('Please enter your name');
            return;
        } else if (!email) {
            toast.warning('Please enter your email');
            return;
        } else if (!phone) {
            toast.warning('Please enter your phone number');
            return;
        }

        setIsProcessing(true);

        try {
            const timePayload = selectedTime ? {
                ...selectedTime,
                hour: selectedHour || selectedTime.hour || ''
            } : null;
            const dateStr = selectedDate ? selectedDate.format('YYYY-MM-DD') : '';

            const formdata = new FormData();
            formdata.append("page", "SaveAppointment");
            formdata.append("image", base64Image);
            formdata.append("insuranceProvider", insuranceProvider);
            formdata.append("email", email);
            formdata.append("name", name);
            formdata.append("phone", phone);
            formdata.append("doctor", JSON.stringify(doctor));
            formdata.append("docMail", doctor?.email || '');
            formdata.append("docName", doctor?.name || '');
            formdata.append("time", JSON.stringify(timePayload));
            formdata.append("date", dateStr);

            const requestOptions = { method: 'POST', body: formdata, redirect: 'follow' };
            const response = await fetch(`${API_LINK}Appointment.php`, requestOptions);
            const raw = await response.text();
            let result;
            try {
                // Try to extract JSON from response that might have SMTP errors prefixed
                const jsonMatch = raw.match(/\{[^}]*"status"[^}]*\}/);
                if (jsonMatch) {
                    result = JSON.parse(jsonMatch[0]);
                } else {
                    result = JSON.parse(raw);
                }
            } catch (e) {
                console.error('Raw server response:', raw);
                toast.error('Unable to parse server response. Please try again.');
                return;
            }

            if (result.status === 1) {
                toast.success("Appointment submitted successfully! Our team will confirm shortly.");
                setName('');
                setEmail('');
                setPhone('');
                setSelectedDate(null);
                setSelectedTime(null);
                setSelectedHour('');
                setSelectedTimeHours([]);
                setStep('1');
                setIsVisible(false);
            } else if (result.status === 2) {
                toast.warning(result.message);
            } else {
                toast.error("Server Error! Please try again later.");
            }
        } catch (error) {
            console.log(error);
            toast.error("Network Error! Please check your connection.");
        } finally {
            setIsProcessing(false);
        }
    };

    const minDate = useMemo(() => moment().startOf('day'), []);

    if (!doctor) {
        return null;
    }

    const handleClose = () => {
        setStep('1');
        setSelectedDate(null);
        setSelectedTime(null);
        setSelectedHour('');
        setSelectedTimeHours([]);
        setIsVisible(false);
    };

    return (
        <Modal
            className="premium-appointment-modal"
            centered={true}
            destroyOnClose={true}
            width="95%"
            style={{ maxWidth: step === '1' ? '800px' : '600px' }}
            title={null}
            zIndex={9999}
            maskClosable={true}
            keyboard={true}
            onCancel={handleClose}
            footer={null}
            open={isVisible}
            mask={true}
            bodyStyle={{ padding: 0, overflow: 'hidden', borderRadius: '16px' }}
        >
            {isProcessing && (
                <div className="premium-processing-overlay">
                    <div className="premium-processing-card">
                        <div className="premium-spinner"></div>
                        <h3>Processing Your Request</h3>
                        <p>Please wait while we book your appointment...</p>
                    </div>
                </div>
            )}

            <div className="premium-modal-container">
                {/* Beautiful Header */}
                <div className="premium-header">
                    <div className="header-gradient"></div>
                    <div className="header-content">
                        <div className="doctor-profile">
                            <div className="doctor-avatar-container">
                                <img src={doctor.image} alt={doctor.name} className="doctor-avatar" />
                                <div className="avatar-ring"></div>
                            </div>
                            <div className="doctor-info">
                                <h2 className="doctor-name">{doctor.name}</h2>
                                <p className="doctor-title">Licensed Therapist</p>
                            </div>
                        </div>
                        
                        <div className="step-indicator">
                            <div className="step-progress">
                                <div className={`step-circle ${step === '1' || step === '2' ? 'active' : ''}`}>
                                    <span>1</span>
                                </div>
                                <div className={`step-connector ${step === '2' ? 'completed' : ''}`}></div>
                                <div className={`step-circle ${step === '2' ? 'active' : ''}`}>
                                    <span>2</span>
                                </div>
                            </div>
                            <div className="step-labels">
                                <span className={step === '1' ? 'active-step' : ''}>Schedule</span>
                                <span className={step === '2' ? 'active-step' : ''}>Details</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Step 1: Date & Time Selection */}
                {step === '1' && (
                    <div className="premium-step-content">
                        <div className="step-intro">
                            <h3 className="step-title">📅 When would you like to schedule?</h3>
                            <p className="step-subtitle">Choose your preferred date and time</p>
                        </div>

                        {/* Current Selection Display */}
                        {(selectedDate || selectedTime || selectedHour) && (
                            <div className="selection-preview">
                                <div className="preview-header">
                                    <span className="preview-icon">✨</span>
                                    <span>Your Selection</span>
                                </div>
                                <div className="preview-items">
                                    {selectedDate && (
                                        <div className="preview-item date-item">
                                            <span className="item-icon">📅</span>
                                            <span className="item-text">{selectedDate.format('dddd, MMMM Do, YYYY')}</span>
                                        </div>
                                    )}
                                    {selectedTime && (
                                        <div className="preview-item time-item">
                                            <span className="item-icon">🕐</span>
                                            <span className="item-text">{selectedTime.time} - {selectedTime.type}</span>
                                        </div>
                                    )}
                                    {selectedHour && (
                                        <div className="preview-item hour-item">
                                            <span className="item-icon">⏰</span>
                                            <span className="item-text">{selectedHour}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Date Selection */}
                        <div className="premium-card">
                            <div className="card-header">
                                <div className="header-icon">📅</div>
                                <div className="header-text">
                                    <h4>Select Date</h4>
                                    <p>Choose an available appointment date</p>
                                </div>
                            </div>
                            <div className="card-body">
                                <DatePicker
                                    value={selectedDate}
                                    format="YYYY-MM-DD"
                                    style={{ width: "100%", height: "50px" }}
                                    size="large"
                                    placeholder="Click to select date"
                                    allowClear={true}
                                    disabled={false}
                                    inputReadOnly={false}
                                    getPopupContainer={(node) => {
                                        // Find the modal body to ensure popup stays within modal
                                        const modalBody = node.closest('.ant-modal-body');
                                        return modalBody || document.body;
                                    }}
                                    onChange={(date) => {
                                        console.log('DatePicker onChange:', date);
                                        if (!date) {
                                            setSelectedDate(null);
                                            setSelectedTime(null);
                                            setSelectedHour('');
                                            setSelectedTimeHours([]);
                                            return;
                                        }
                                        
                                        // Ensure we're working with a moment object
                                        const momentDate = moment.isMoment(date) ? date : moment(date);
                                        const normalized = momentDate.startOf('day');
                                        console.log('Setting date:', normalized.format('YYYY-MM-DD'));
                                        
                                        setSelectedDate(normalized);
                                        setSelectedTime(null);
                                        setSelectedHour('');
                                        setSelectedTimeHours([]);
                                    }}
                                    disabledDate={(current) => {
                                        if (!current || !doctor || !doctor.availableTime) return false;
                                        
                                        // Ensure current is a moment object and get available days
                                        const currentMoment = moment.isMoment(current) ? current : moment(current);
                                        const availableDays = doctor.availableTime.map(time => time.day);
                                        const dayName = currentMoment.format('dddd');
                                        
                                        // Disable if not available day or before today
                                        return !availableDays.includes(dayName) || currentMoment.isBefore(minDate);
                                    }}
                                />
                            </div>
                        </div>

                        {/* Time Slots - Only show after date is selected */}
                        {selectedDate && doctor.availableTime && (
                            <div className="premium-card" ref={timeSlotsRef}>
                                <div className="card-header">
                                    <div className="header-icon">🕐</div>
                                    <div className="header-text">
                                        <h4>Available Time Slots</h4>
                                        <p>Select your preferred session time for {selectedDate.format('dddd')}</p>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div className="time-slot-grid">
                                        {doctor.availableTime
                                            .filter(time => time.day === selectedDate.format('dddd'))
                                            .map((time, index) => (
                                                <div
                                                    key={index}
                                                    className={`time-slot-card ${selectedTime && selectedTime.day === time.day ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        if (selectedTime && selectedTime.day === time.day) {
                                                            setSelectedTime(null);
                                                            setSelectedHour('');
                                                            setSelectedTimeHours([]);
                                                        } else {
                                                            setSelectedTime(time);
                                                            setSelectedHour('');
                                                            setSelectedTimeHours(time.hours || []);
                                                        }
                                                    }}
                                                >
                                                    <div className="slot-header">
                                                        <span className="slot-day">{time.day}</span>
                                                        <div className="slot-check">
                                                            {selectedTime && selectedTime.day === time.day && (
                                                                <span className="check-icon">✓</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="slot-body">
                                                        <span className="slot-time">{time.time}</span>
                                                        <span className="slot-type">{time.type}</span>
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Hour Selection - Only show after time slot is selected */}
                        {selectedTime && selectedTimeHours.length > 0 && (
                            <div className="premium-card" ref={hoursRef}>
                                <div className="card-header">
                                    <div className="header-icon">⏰</div>
                                    <div className="header-text">
                                        <h4>Select Specific Hour</h4>
                                        <p>Choose your exact appointment time</p>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div className="hour-grid">
                                        {selectedTimeHours.map((hour, index) => (
                                            <button
                                                key={index}
                                                className={`hour-button ${selectedHour === hour ? 'selected' : ''}`}
                                                onClick={() => {
                                                    if (selectedHour === hour) {
                                                        setSelectedHour('');
                                                        setSelectedTime((prev) => ({ ...prev, hour: null }));
                                                    } else {
                                                        setSelectedHour(hour);
                                                        setSelectedTime((prev) => ({ ...prev, hour }));
                                                    }
                                                }}
                                            >
                                                <span className="hour-text">{hour}</span>
                                                {selectedHour === hour && (
                                                    <div className="hour-selected-indicator">
                                                        <span>✓</span>
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Insurance & Consent - Only show after hour is selected */}
                        {selectedHour && (
                            <div className="premium-card" ref={insuranceRef}>
                            <div className="card-header">
                                <div className="header-icon">🏥</div>
                                <div className="header-text">
                                    <h4>Insurance & Consent</h4>
                                    <p>Final details before we continue</p>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="insurance-section">
                                    <div className="insurance-question">
                                        <span className="question-text">Do you have insurance?</span>
                                        <Switch
                                            checkedChildren="Yes"
                                            unCheckedChildren="No"
                                            checked={isInsurance}
                                            onChange={setIsInsurance}
                                        />
                                    </div>
                                    {isInsurance && (
                                        <div className="insurance-input-container">
                                            <input
                                                type="text"
                                                className="premium-input"
                                                placeholder="Enter your insurance provider name"
                                                value={insuranceProvider}
                                                onChange={(e) => setInsuranceProvider(e.target.value)}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="consent-section">
                                    <div className="consent-item">
                                        <Checkbox
                                            checked={consent1}
                                            onChange={(e) => setConsent1(e.target.checked)}
                                        />
                                        <span className="consent-text">
                                            I consent to receive communications for scheduling and support. 
                                            Contact: <a href="tel:404-795-6474" className="phone-link">404-795-6474</a>
                                        </span>
                                    </div>
                                    <div className="consent-item">
                                        <Checkbox
                                            checked={consent2}
                                            onChange={(e) => setConsent2(e.target.checked)}
                                        />
                                        <span className="consent-text">
                                            I consent to SMS messages. Reply STOP to opt out, HELP for info.
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Continue Button - Only show after hour is selected */}
                        {selectedHour && (
                            <div className="premium-actions" ref={continueRef}>
                                <button type="button" className="btn-secondary" onClick={handleClose}>
                                    Cancel
                                </button>
                                <button type="button" className="btn-primary-gradient" onClick={submitNext}>
                                    <span>Continue</span>
                                    <span className="btn-icon">→</span>
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Step 2: Personal Details */}
                {step === '2' && (
                    <div className="premium-step-content">
                        <div className="step-intro">
                            <h3 className="step-title">👤 Almost there!</h3>
                            <p className="step-subtitle">Just a few details to complete your booking</p>
                        </div>

                        {/* Appointment Summary */}
                        <div className="appointment-summary-card">
                            <div className="summary-header">
                                <span className="summary-icon">📋</span>
                                <span>Appointment Summary</span>
                            </div>
                            <div className="summary-content">
                                <div className="summary-row">
                                    <span className="label">Date:</span>
                                    <span className="value">{selectedDate?.format('dddd, MMM Do, YYYY')}</span>
                                </div>
                                <div className="summary-row">
                                    <span className="label">Time:</span>
                                    <span className="value">{selectedTime?.time} ({selectedTime?.type})</span>
                                </div>
                                {selectedHour && (
                                    <div className="summary-row">
                                        <span className="label">Hour:</span>
                                        <span className="value">{selectedHour}</span>
                                    </div>
                                )}
                                <div className="summary-row">
                                    <span className="label">Therapist:</span>
                                    <span className="value">{doctor.name}</span>
                                </div>
                            </div>
                        </div>

                        {/* Personal Details Form */}
                        <div className="premium-card">
                            <div className="card-header">
                                <div className="header-icon">👤</div>
                                <div className="header-text">
                                    <h4>Your Information</h4>
                                    <p>Tell us how to reach you</p>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="form-grid-premium">
                                    <div className="form-group-premium">
                                        <label className="premium-label">
                                            <span className="label-icon">📝</span>
                                            <span>Full Name *</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="premium-input"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder="Enter your full name"
                                            required
                                        />
                                    </div>

                                    <div className="form-group-premium">
                                        <label className="premium-label">
                                            <span className="label-icon">📧</span>
                                            <span>Email Address *</span>
                                        </label>
                                        <input
                                            type="email"
                                            className="premium-input"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="your.email@example.com"
                                            required
                                        />
                                    </div>

                                    <div className="form-group-premium">
                                        <label className="premium-label">
                                            <span className="label-icon">📞</span>
                                            <span>Phone Number *</span>
                                        </label>
                                        <input
                                            type="tel"
                                            className="premium-input"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            placeholder="(555) 123-4567"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="premium-actions">
                            <button type="button" className="btn-secondary" onClick={() => setStep('1')}>
                                <span className="btn-icon">←</span>
                                <span>Back</span>
                            </button>
                            <button type="button" className="btn-success-gradient" onClick={submitAppointment}>
                                <span>🎯 Book Appointment</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}